/**
 * ============================================================================
 * DIALOGS STORE - CREATE OPERATIONS
 * ============================================================================
 * 
 * Handles creating new dialogs via the backend API.
 * Automatically processes translatable fields to create TMS strings.
 * 
 * @module dialogs/api/create
 */

import { apiService } from "@/lib/api-functions/api-service";
import { createString } from "../../stringsStore";
import { dialogStore } from "../store";
import { processDialogForTranslation } from "../utils/translation";
import type { Dialog, CreateDialogData, ToastFunction } from "../types";

// ============================================================================
// CREATE OPERATIONS
// ============================================================================

/**
 * Create a new dialog via API and sync the store.
 * 
 * Process:
 * 1. Processes translatable fields to create string records
 * 2. Sends the processed dialog to the API
 * 3. Adds the created dialog to the local store
 * 
 * @param dialog - The dialog data to create (with sections in data property)
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to the created dialog
 * 
 * @example
 * ```ts
 * const newDialog = await createDialog({
 *   game_id: 'game-123',
 *   name: 'Tutorial Dialog',
 *   dialog_key: 'tutorial_intro',
 *   tags: ['tutorial'],
 *   data: {
 *     sections: [...],
 *     speakerMode: 'type',
 *   },
 * });
 * ```
 */
export async function createDialog(
  dialog: CreateDialogData,
  toast?: ToastFunction
): Promise<Dialog> {
  try {
    // Process translatable fields and options to create strings
    const { dialog: processedDialog } = await processDialogForTranslation(
      dialog,
      createString
    );

    // Sync string_ids from processed sections to nodes
    // (same as update.ts - prevents backend from creating duplicate strings)
    if (processedDialog.data?.nodes && processedDialog.data?.sections) {
      const fieldStringIdMap = new Map<string, string>();
      const fieldOptionsMap = new Map<string, any[]>();
      for (const section of processedDialog.data.sections) {
        if (section.fields) {
          for (const field of section.fields) {
            if (field.string_id) {
              fieldStringIdMap.set(field.id, field.string_id);
            }
            // Also track options with string_ids for syncing to nodes
            if (field.options && field.options.length > 0) {
              fieldOptionsMap.set(field.id, field.options);
            }
          }
        }
      }
      if (fieldStringIdMap.size > 0 || fieldOptionsMap.size > 0) {
        processedDialog.data.nodes = processedDialog.data.nodes.map((node: any) => {
          let updatedNode = node;
          // Sync field-level string_id
          const stringId = fieldStringIdMap.get(node.id);
          if (stringId && !node.string_id) {
            updatedNode = { ...updatedNode, string_id: stringId };
          }
          // Sync option-level string_ids
          const processedOptions = fieldOptionsMap.get(node.id);
          if (processedOptions && node.options) {
            const updatedOptions = node.options.map((nodeOpt: any, idx: number) => {
              const processedOpt = processedOptions[idx];
              if (processedOpt?.string_id && !nodeOpt.string_id) {
                return { ...nodeOpt, string_id: processedOpt.string_id };
              }
              return nodeOpt;
            });
            updatedNode = { ...updatedNode, options: updatedOptions };
          }
          return updatedNode;
        });
      }
    }

    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Create via API using the processed dialog
    const result = await apiService.createDialog(processedDialog);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create dialog');
    }

    const newDialog = result.data;

    // Add the new dialog to the store (immutably)
    const currentData = dialogStore.getData();
    const updatedData = [...currentData, newDialog];
    dialogStore.setData(updatedData);

    console.log(`[createDialog] Created dialog: ${newDialog.id} - ${newDialog.name}`);
    return newDialog;
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create dialog';
    console.error('[createDialog] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
